/**
 * Recurrence engine (contract §5). Pure functions, no I/O, no Date.now().
 *
 * All date math is done on local calendar dates in `yyyy-mm-dd` form.
 * Dates are anchored at UTC noon so adding days never crosses a DST edge:
 * the calendar-day sequence is identical in every timezone.
 */
import type { RecurrenceRule } from '@/types'

export type OccurrenceDate = {
  /** Local calendar date, ISO yyyy-mm-dd. */
  localDate: string
  /** Local wall-clock time, HH:mm (passed through unchanged). */
  localTime: string
}

export type ComputeOccurrenceInput = {
  /** First possible occurrence date, yyyy-mm-dd. */
  startDate: string
  /** Last possible occurrence date, yyyy-mm-dd, or null for no end. */
  endDate: string | null
  /** Local wall-clock time, HH:mm. */
  localTime: string
  rule: RecurrenceRule
  /** Bounded window start, yyyy-mm-dd. Mandatory — never unbounded. */
  rangeStart: string
  /** Bounded window end, yyyy-mm-dd. Mandatory — never unbounded. */
  rangeEnd: string
}

const DAY_MS = 86_400_000
const DATE_RE = /^(\d{4})-(\d{2})-(\d{2})$/

/** Number of milliseconds in the bounded window we are willing to iterate. */
const MAX_WINDOW_DAYS = 366 * 5

function parseLocalDate(date: string): { year: number; month: number; day: number } {
  const match = DATE_RE.exec(date)
  if (!match) {
    throw new Error(`Ongeldige datum (verwacht yyyy-mm-dd): ${date}`)
  }
  const year = Number(match[1])
  const month = Number(match[2])
  const day = Number(match[3])
  if (month < 1 || month > 12 || day < 1 || day > 31) {
    throw new Error(`Ongeldige datum (verwacht yyyy-mm-dd): ${date}`)
  }
  return { year, month, day }
}

/** UTC-noon anchor for a local calendar date — DST-safe arithmetic base. */
function toAnchorMs(date: string): number {
  const { year, month, day } = parseLocalDate(date)
  return Date.UTC(year, month - 1, day, 12)
}

function fromAnchorMs(ms: number): string {
  const d = new Date(ms)
  const year = d.getUTCFullYear()
  const month = String(d.getUTCMonth() + 1).padStart(2, '0')
  const day = String(d.getUTCDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

/** Adds whole calendar days to a yyyy-mm-dd date, DST-safe. */
export function addLocalDays(date: string, days: number): string {
  return fromAnchorMs(toAnchorMs(date) + days * DAY_MS)
}

/** Weekday of a yyyy-mm-dd date: 0 = Sunday … 6 = Saturday. */
export function weekdayOf(date: string): number {
  return new Date(toAnchorMs(date)).getUTCDay()
}

function dayOfMonthOf(date: string): number {
  return parseLocalDate(date).day
}

function daysInMonth(year: number, month: number): number {
  // Day 0 of the next month = last day of this month (UTC, so DST-safe).
  return new Date(Date.UTC(year, month, 0)).getUTCDate()
}

/** Today's local calendar date (yyyy-mm-dd) in the device's timezone. */
export function todayLocalDate(now: Date = new Date()): string {
  const year = now.getFullYear()
  const month = String(now.getMonth() + 1).padStart(2, '0')
  const day = String(now.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

/**
 * Normalizes a weekly weekday selection: dedupes, keeps 0–6, and falls back
 * to the weekday of the start date when nothing valid is selected.
 */
function normalizeWeekdays(weekdays: number[] | undefined, startDate: string): Set<number> {
  const valid = new Set((weekdays ?? []).filter((d) => Number.isInteger(d) && d >= 0 && d <= 6))
  if (valid.size === 0) {
    valid.add(weekdayOf(startDate))
  }
  return valid
}

/**
 * Computes the occurrence dates of a schedule inside a bounded window.
 *
 * - The window is intersected with [startDate, endDate ?? ∞]; both range
 *   bounds are mandatory so results are never unbounded.
 * - `once` yields at most the start date itself.
 * - `weekly` matches the selected weekdays (0 = Sunday … 6 = Saturday).
 * - `monthly` matches `dayOfMonth`; months without that day (e.g. February
 *   for day 31) are skipped — never rolled into the next month.
 * - Duplicate dates are never returned.
 */
export function computeOccurrenceDates(input: ComputeOccurrenceInput): OccurrenceDate[] {
  const { startDate, endDate, localTime, rule, rangeStart, rangeEnd } = input

  if (rangeEnd < rangeStart) return []

  const windowStart = startDate > rangeStart ? startDate : rangeStart
  const windowEnd = endDate !== null && endDate < rangeEnd ? endDate : rangeEnd
  if (windowEnd < windowStart) return []

  const startMs = toAnchorMs(windowStart)
  const endMs = toAnchorMs(windowEnd)
  if (endMs - startMs > MAX_WINDOW_DAYS * DAY_MS) {
    throw new Error('Het datumbereik is te groot. Kies een kleinere periode.')
  }

  const dates: string[] = []

  switch (rule.type) {
    case 'once': {
      if (startDate >= windowStart && startDate <= windowEnd) {
        dates.push(startDate)
      }
      break
    }
    case 'daily': {
      for (let d = windowStart; d <= windowEnd; d = addLocalDays(d, 1)) {
        dates.push(d)
      }
      break
    }
    case 'weekly': {
      const weekdays = normalizeWeekdays(rule.weekdays, startDate)
      for (let d = windowStart; d <= windowEnd; d = addLocalDays(d, 1)) {
        if (weekdays.has(weekdayOf(d))) {
          dates.push(d)
        }
      }
      break
    }
    case 'monthly': {
      const dayOfMonth = rule.dayOfMonth ?? dayOfMonthOf(startDate)
      if (dayOfMonth < 1 || dayOfMonth > 31) break
      let { year, month } = parseLocalDate(windowStart)
      for (;;) {
        if (dayOfMonth <= daysInMonth(year, month)) {
          const candidate = `${year}-${String(month).padStart(2, '0')}-${String(dayOfMonth).padStart(2, '0')}`
          if (candidate > windowEnd) break
          if (candidate >= windowStart) {
            dates.push(candidate)
          }
        } else if (`${year}-${String(month).padStart(2, '0')}-01` > windowEnd) {
          break
        }
        month += 1
        if (month > 12) {
          month = 1
          year += 1
        }
      }
      break
    }
  }

  return dates.map((localDate) => ({ localDate, localTime }))
}

// ---------------------------------------------------------------------------
// Dutch preview text
// ---------------------------------------------------------------------------

const WEEKDAY_NAMES_NL = [
  'zondag',
  'maandag',
  'dinsdag',
  'woensdag',
  'donderdag',
  'vrijdag',
  'zaterdag',
] as const

const MONTH_NAMES_NL = [
  'januari',
  'februari',
  'maart',
  'april',
  'mei',
  'juni',
  'juli',
  'augustus',
  'september',
  'oktober',
  'november',
  'december',
] as const

/** Formats yyyy-mm-dd as Dutch long date, e.g. `5 maart 2026`. */
export function formatDutchDate(date: string): string {
  const { year, month, day } = parseLocalDate(date)
  return `${day} ${MONTH_NAMES_NL[month - 1]} ${year}`
}

/** `['a']` → `a`, `['a','b']` → `a en b`, `['a','b','c']` → `a, b en c`. */
function joinDutch(parts: string[]): string {
  if (parts.length <= 1) return parts[0] ?? ''
  return `${parts.slice(0, -1).join(', ')} en ${parts[parts.length - 1]}`
}

/**
 * Human-readable Dutch description of a recurrence rule, including the
 * documented monthly skip rule when the chosen day does not exist in every
 * month (day 29–31).
 */
export function describeRecurrence(rule: RecurrenceRule, startDate: string): string {
  const start = formatDutchDate(startDate)
  switch (rule.type) {
    case 'once':
      return `Eenmalig op ${start}`
    case 'daily':
      return `Dagelijks vanaf ${start}`
    case 'weekly': {
      const weekdays = [...normalizeWeekdays(rule.weekdays, startDate)].sort((a, b) => a - b)
      const names = weekdays.map((d) => WEEKDAY_NAMES_NL[d])
      return `Wekelijks op ${joinDutch(names)} vanaf ${start}`
    }
    case 'monthly': {
      const dayOfMonth = rule.dayOfMonth ?? dayOfMonthOf(startDate)
      const base = `Maandelijks op dag ${dayOfMonth} vanaf ${start}`
      if (dayOfMonth > 28) {
        return `${base}. Maanden zonder dag ${dayOfMonth} (bijvoorbeeld februari) worden overgeslagen.`
      }
      return base
    }
  }
}
